import { Dispatch, SetStateAction, useState, useEffect, useRef } from "react";
import FileTree from "files/components/FileTree";
import { resolveRouterPath } from "files/hooks/useFiles";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";

interface FileSelectShadcnProps {
  selectedFile: FileInfo | null;
  setSelectedFile: Dispatch<SetStateAction<FileInfo | null>>;
  onSelectFile: (file: FileInfo) => void;
  maxHeight?: number;
  placeholder?: string;
  className?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether file info is being loaded (shows loading indicator) */
  isLoading?: boolean;
  /** Placeholder text when loading */
  loadingPlaceholder?: string;
}

/**
 * Collapsible file picker wrapping MUI FileTree with shadcn/ui styling.
 * Auto-closes when user selects a file (not directory).
 */
const FileSelectShadcn = ({
  selectedFile,
  setSelectedFile,
  onSelectFile,
  placeholder = "Select source file...",
  className,
  disabled = false,
  isLoading = false,
  loadingPlaceholder = "Loading file information...",
}: FileSelectShadcnProps) => {
  const [open, setOpen] = useState(false);
  const fileKeyOnOpenRef = useRef<string | null>(null);
  const wasOpenRef = useRef(false);

  // Capture file key when opening to detect new selections
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      fileKeyOnOpenRef.current = selectedFile?.key ?? null;
    }
    wasOpenRef.current = open;
  }, [open, selectedFile?.key]);

  // Auto-close when user selects a different file
  useEffect(() => {
    if (
      open &&
      selectedFile?.type === FileType.FILE &&
      selectedFile.key !== fileKeyOnOpenRef.current
    ) {
      onSelectFile(selectedFile);
      setOpen(false);
    }
  }, [selectedFile, selectedFile?.key, onSelectFile, open]);

  // Determine display text
  const displayFile = selectedFile?.type === FileType.FILE ? selectedFile : null;
  const displayText = isLoading
    ? loadingPlaceholder
    : displayFile
    ? resolveRouterPath(displayFile.key)
    : placeholder;

  // Prevent opening when disabled or loading
  const handleOpenChange = (newOpen: boolean) => {
    if (!disabled && !isLoading) {
      setOpen(newOpen);
    }
  };

  return (
    <Collapsible open={open} onOpenChange={handleOpenChange} className={cn("w-full", className)}>
      <CollapsibleTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled || isLoading}
          className={cn(
            "w-full justify-between text-left font-normal h-auto min-h-11 py-2",
            !displayFile && !isLoading && "text-muted-foreground",
            isLoading && "text-muted-foreground cursor-wait",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          <span className="truncate flex-1 text-left flex items-center gap-2">
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {displayText}
          </span>
          {!isLoading &&
            (open ? (
              <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            ) : (
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            ))}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="rounded-lg border bg-white p-4">
          {/* FileTree (MUI) wrapped in shadcn container */}
          <FileTree
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            showFiles
            maxHeight={200}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default FileSelectShadcn;
